import { useState } from "react";
import { Link } from "react-router-dom";
import { CaretDown } from "@phosphor-icons/react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";

const FAQ_SECTIONS = [
    {
        section: "Members",
        items: [
            {
                q: "Is SocietyIntent free to join?",
                a: "Yes — joining is and always will be free. We only make money from the brands that pay to run surveys, never from members. There are no premium tiers, no upsells, no subscriptions.",
            },
            {
                q: "How much can I really earn?",
                a: "Casual users typically earn $15–$40 per month. Power users in sought-after demographics can earn $100+. We won't quote per-hour figures because survey availability genuinely depends on who you are. See the help article on realistic earnings.",
                link: { to: "/help/how-much-can-i-earn", label: "Read the full earnings guide" },
            },
            {
                q: "How and when do I get paid?",
                a: "100 points = $1 USD. Cash out any time at 1,000 points ($10) minimum via PayPal, Stripe direct deposit, or gift card. Most payouts arrive within 48 hours of your request.",
                link: { to: "/help/how-cashout-works", label: "Cashout details" },
            },
            {
                q: "Do you sell my personal information?",
                a: "No. We do not sell, rent, lease, trade, or otherwise transfer for consideration any of your personal information — including your name, email, phone number, postal address, payment details, demographic data, or survey responses. Read the new \"No Sale of Personal Information\" section in our Terms.",
                link: { to: "/legal/terms", label: "Read the Terms" },
            },
            {
                q: "What countries do you currently support?",
                a: "USA and Canada at launch. Members must be at least 18 and a legal resident. We have a waitlist for the UK, Australia, and a small set of EU countries.",
            },
        ],
    },
    {
        section: "Surveys & quality",
        items: [
            {
                q: "Why do some surveys disqualify me?",
                a: "Two different things can happen. (1) You're screened out because the brand was looking for a different demographic — you still get a small consolation payout. (2) You're disqualified for quality reasons (failed attention check, finished too fast, duplicate signals) — no payout in that case.",
                link: { to: "/help/qualification-vs-disqualification", label: "Full breakdown" },
            },
            {
                q: "What's an attention check?",
                a: "A simple question inside a longer survey, typically phrased like \"please select Tuesday.\" It exists to catch people who skim without reading. Pick the option the question literally asks for.",
            },
            {
                q: "How fast is too fast?",
                a: "We require at least 3 seconds per question with a 5-second total minimum. Per-question speed checks fire when the average gap between consecutive answers drops below 1.5 seconds.",
                link: { to: "/help/what-makes-a-response-invalid", label: "Anti-fraud signals" },
            },
            {
                q: "I think my response was wrongly marked invalid — what now?",
                a: "Email appeals@societyintent.com with the survey title and rough date you took it. Most appeals are reviewed within ten business days.",
            },
        ],
    },
    {
        section: "Advertisers",
        items: [
            {
                q: "What does it cost to run a study?",
                a: "Respondent payout (typically $1 per complete) plus a 25% platform fee. So a 200-response study costs $250 total — flat.",
                link: { to: "/for-advertisers", label: "See pricing details" },
            },
            {
                q: "How long does survey review take?",
                a: "Most surveys are reviewed within two business hours during weekday working hours. Surveys with PII issues, complex branching, or unusual sponsorship may take 24–48 hours.",
                link: { to: "/help/advertiser-survey-review-process", label: "What we check" },
            },
            {
                q: "What kind of payment do you accept?",
                a: "Stripe Checkout — all major credit and debit cards, plus Apple Pay and Google Pay. Currently in test mode while we finalize live processing; no real charges yet.",
            },
            {
                q: "Can I see member-level data?",
                a: "No. Advertisers receive pseudonymized responses (no name, email, phone, IP, or precise location) and aggregated demographic breakdowns only when the cell size is ≥ 10. This protects respondent privacy.",
                link: { to: "/help/what-advertisers-can-see", label: "Exact field list" },
            },
            {
                q: "Can I get a refund?",
                a: "Yes — if your study is rejected during review your funds remain on hold so you can fix and resubmit without re-paying. If responses are unfilled at study close, the unused portion is refunded within five business days.",
            },
        ],
    },
    {
        section: "Privacy & data",
        items: [
            {
                q: "Where is my data stored?",
                a: "In the United States, on infrastructure operated by AWS (us-east-1) and MongoDB Atlas. By using SocietyIntent from outside the U.S. you consent to the transfer of your data to the U.S.",
            },
            {
                q: "Can I download or delete my data?",
                a: "Yes. Profile → Privacy & Data → Download my data (JSON export within 24 hours) or Delete my account (soft-delete immediately, hard-delete after a 30-day cool-off).",
                link: { to: "/help/exporting-or-deleting-your-data", label: "Step-by-step" },
            },
            {
                q: "Do you use any tracking cookies?",
                a: "No. We use only first-party, strictly-necessary cookies for authentication and security. No analytics, marketing, or social-media trackers — read the Cookie Policy.",
                link: { to: "/legal/cookies", label: "Cookie Policy" },
            },
        ],
    },
];

export default function FAQ() {
    return (
        <div className="bg-paper text-ink min-h-screen">
            <Navbar />
            <section className="max-w-3xl mx-auto px-6 py-16" data-testid="faq-page">
                <p className="eyebrow mb-3">FAQ</p>
                <h1 className="h1 mb-3">Frequently asked questions</h1>
                <p className="text-ink-600 mb-12 leading-relaxed">
                    The short answers. For deeper dives, browse the{" "}
                    <Link to="/help" className="underline" data-testid="faq-help-link">help center</Link>
                    {" "}or{" "}
                    <Link to="/contact" className="underline" data-testid="faq-contact-link">contact us</Link>.
                </p>

                <div className="space-y-12">
                    {FAQ_SECTIONS.map((sec) => (
                        <section key={sec.section} data-testid={`faq-section-${slugify(sec.section)}`}>
                            <h2 className="text-sm uppercase tracking-wider text-ink-600 mb-5">{sec.section}</h2>
                            <div className="border border-ink-200 divide-y divide-ink-200">
                                {sec.items.map((item, idx) => (
                                    <FAQItem key={idx} item={item} testid={`faq-item-${slugify(sec.section)}-${idx}`} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </section>
            <Footer />
        </div>
    );
}

function FAQItem({ item, testid }) {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid={testid}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-4 text-left p-5 hover:bg-ink-50 transition-colors"
                aria-expanded={open}
                data-testid={`${testid}-toggle`}
            >
                <span className="font-semibold text-base">{item.q}</span>
                <CaretDown
                    size={18}
                    className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    weight="bold"
                />
            </button>
            {open && (
                <div className="px-5 pb-5 -mt-1 text-ink-600 leading-relaxed text-sm space-y-2" data-testid={`${testid}-answer`}>
                    <p>{item.a}</p>
                    {item.link && (
                        <p>
                            <Link to={item.link.to} className="underline">{item.link.label}</Link>
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

function slugify(s) {
    return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
